import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Grid, List, X, Check, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ProductCard } from '../components/ProductCard';
import { RewardCard } from '../components/RewardCard';
import SEO from '../components/SEO';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;


export default function CategoryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rewards, setRewards] = useState([]);
  const [isGiftCardCategory, setIsGiftCardCategory] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);
    return pages;
  };

  // Reset page when slug or search params change (except when appending)
  useEffect(() => {
    setPage(1);
  }, [slug, searchParams.get('sort'), searchParams.get('minPrice'), searchParams.get('maxPrice'), searchParams.get('q')]);

  const sort = searchParams.get('sort') || 'newest';
  const minPrice = searchParams.get('minPrice');
  const maxPrice = searchParams.get('maxPrice');
  const brands = searchParams.get('brands')?.split(',').filter(Boolean) || [];

  const query = searchParams.get('q');
  const isFeatured = searchParams.get('featured');
  const isBestseller = searchParams.get('bestseller');
  const isTrending = searchParams.get('trending');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const isGiftCards = slug === 'gift-cards';
        setIsGiftCardCategory(isGiftCards);

        if (isGiftCards) {
          // Fetch Razorpay rewards instead of normal products
          const rewardResponse = await fetch(`${API}/giftcards/razorpay/rewards`, {
            credentials: 'include'
          });
          if (rewardResponse.ok) {
            const rewardData = await rewardResponse.json();
            // Filter to show only gift cards and avoid testing offers
            const giftCards = (rewardData.items || []).filter(item => item.type === 'gift_card');
            setRewards(giftCards);
            setLoading(false);
            return;
          }
        }

        let categoryId = null;

        if (slug) {
          // Fetch category if slug is provided
          const catResponse = await fetch(`${API}/categories/slug/${slug}`);
          if (!catResponse.ok) throw new Error('Category not found');
          const catData = await catResponse.json();
          setCategory(catData);
          categoryId = catData.category_id;
        } else {
          setCategory(null);
        }

        // Then fetch products
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '12',
          sort
        });

        if (categoryId) params.append('category', categoryId);
        if (minPrice) params.append('min_price', minPrice);
        if (maxPrice) params.append('max_price', maxPrice);
        if (query) params.append('search', query);
        if (isFeatured) params.append('featured', isFeatured);
        if (isBestseller) params.append('bestseller', isBestseller);
        if (isTrending) params.append('trending', isTrending);

        const prodResponse = await fetch(`${API}/products?${params}`);
        const prodData = await prodResponse.json();

        setProducts(prodData.products || []);
        setTotalPages(prodData.pages || 1);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setProducts([]);
        if (slug) setCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, page, sort, minPrice, maxPrice, query, isFeatured, isBestseller, isTrending]);



  const handleSortChange = (value) => {
    searchParams.set('sort', value);
    setSearchParams(searchParams);
  };

  const handlePriceFilter = (min, max) => {
    const currentMin = searchParams.get('minPrice');
    const currentMax = searchParams.get('maxPrice');

    // If same range is selected, untoggle it
    if (String(min) === currentMin && (max === null ? !currentMax : String(max) === currentMax)) {
      searchParams.delete('minPrice');
      searchParams.delete('maxPrice');
    } else {
      if (min !== null) searchParams.set('minPrice', min);
      else searchParams.delete('minPrice');

      if (max !== null) searchParams.set('maxPrice', max);
      else searchParams.delete('maxPrice');
    }

    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({ sort: 'newest' });
  };

  const priceRanges = [
    { label: 'Under ₹1,000', min: 0, max: 1000 },
    { label: '₹1,000 - ₹5,000', min: 1000, max: 5000 },
    { label: '₹5,000 - ₹20,000', min: 5000, max: 20000 },
    { label: '₹20,000 - ₹50,000', min: 20000, max: 50000 },
    { label: 'Above ₹50,000', min: 50000, max: null }
  ];


  const formatPageTitle = (text) => {
    if (!text) return '';
    return text.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const isGiftCards = slug === 'gift-cards';
  const rawTitle = isGiftCards ? 'Gift Cards' : (category?.name || (query ? `Search: ${query}` : slug || (isFeatured ? 'Featured Products' : isBestseller ? 'Best Sellers' : isTrending ? 'Trending Now' : 'All Products')));
  const pageTitle = formatPageTitle(rawTitle);

  const pageDescription = isGiftCards 
    ? 'Discover our premium range of digital gift cards for your favorite brands.' 
    : (category?.description || (query ? `Results for "${query}"` : 'Discover our premium collection of products.'));

  return (
    <div className="min-h-screen bg-white" data-testid="category-page">
      <SEO 
        title={pageTitle} 
        description={pageDescription} 
      />
      {/* Hero Section */}
      <section className="bg-surface py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="text-sm uppercase tracking-widest text-primary font-body font-semibold mb-2 block">
            Shop Collection
          </span>
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight text-secondary mb-4">
            {pageTitle}
          </h1>
          <p className="text-lg text-muted-foreground font-body max-w-2xl">
            {pageDescription}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0 sticky top-24 self-start" data-testid="filters-sidebar">
            <div className="bg-surface/50 rounded-[2rem] p-8 border border-gray-100">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5 text-secondary" />
                  <h3 className="font-heading text-xl text-secondary">Filters</h3>
                </div>
                {(minPrice || maxPrice) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs font-body font-bold text-primary hover:text-primary/80 transition-colors uppercase tracking-widest"
                  >
                    Reset
                  </button>
                )}
              </div>

              {/* Price Filter */}
              <div className="space-y-6">
                <div>
                  <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
                    Price Range
                  </h4>
                  <div className="space-y-2">
                    {priceRanges.map((range, idx) => {
                      const isActive = minPrice === String(range.min) && (range.max ? maxPrice === String(range.max) : !maxPrice);
                      return (
                        <button
                          key={idx}
                          onClick={() => handlePriceFilter(range.min, range.max)}
                          className={`w-full flex items-center justify-between group px-4 py-3 rounded-xl transition-all font-body text-sm ${isActive
                            ? 'bg-primary text-white shadow-md'
                            : 'bg-white hover:bg-white text-muted-foreground hover:text-secondary border border-transparent hover:border-gray-100'
                            }`}
                        >
                          <span>{range.label}</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${isActive ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-100">
                  <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-4">Availability</h4>
                  <label className="flex items-center justify-between group cursor-pointer">
                    <span className="text-sm text-muted-foreground group-hover:text-secondary font-body">In Stock Only</span>
                    <Checkbox checked={true} />
                  </label>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
              <div className="flex items-center gap-6">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden flex items-center gap-2 px-6 py-3 bg-surface rounded-full text-sm font-body font-semibold text-secondary hover:bg-gray-100 transition-colors"
                  data-testid="mobile-filters-btn"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filter
                </button>
                <div className="hidden sm:block">
                  <p className="text-sm text-muted-foreground font-body">
                    Showing <span className="font-semibold text-secondary">{isGiftCardCategory ? rewards.length : products.length}</span> {isGiftCardCategory ? 'items' : 'products'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="hidden sm:block text-sm text-muted-foreground font-body">Sort by:</span>
                <Select value={sort} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-[180px] rounded-full bg-surface border-0 h-11 px-6 font-body text-sm" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl shadow-xl border-gray-100">
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Best Rating</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Mobile Filters Modal */}
            {showFilters && (
              <div className="lg:hidden fixed inset-0 z-50 bg-white animate-in slide-in-from-bottom duration-300" data-testid="mobile-filters-modal">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5 text-secondary" />
                    <h3 className="font-heading text-xl text-secondary">Filters</h3>
                  </div>
                  <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-surface rounded-full">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(100vh-140px)]">
                  {/* Price Filter */}
                  <div className="mb-10">
                    <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-6">Price Range</h4>
                    <div className="space-y-3">
                      {priceRanges.map((range, idx) => {
                        const isActive = minPrice === String(range.min) && (range.max ? maxPrice === String(range.max) : !maxPrice);
                        return (
                          <button
                            key={idx}
                            onClick={() => handlePriceFilter(range.min, range.max)}
                            className={`w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all font-body ${isActive
                              ? 'bg-primary text-white shadow-lg'
                              : 'bg-surface text-secondary border border-transparent'
                              }`}
                          >
                            <span className="font-medium">{range.label}</span>
                            {isActive && <Check className="w-5 h-5" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="pt-8 border-t border-gray-100">
                    <h4 className="font-body font-bold text-secondary text-sm uppercase tracking-wider mb-6">Other Options</h4>
                    <div className="space-y-4">
                      <label className="flex items-center justify-between p-4 bg-surface rounded-2xl">
                        <span className="font-body font-medium text-secondary text-sm">In Stock Only</span>
                        <Checkbox checked={true} />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      className="flex-1 h-14 rounded-full font-body font-semibold border-gray-200"
                      onClick={clearFilters}
                    >
                      Reset All
                    </Button>
                    <Button
                      className="flex-1 h-14 rounded-full bg-primary font-body font-semibold shadow-lg shadow-primary/20"
                      onClick={() => setShowFilters(false)}
                    >
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Products/Rewards Grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className="aspect-square bg-surface rounded-3xl animate-pulse" />
                ))}
              </div>
            ) : isGiftCardCategory ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="rewards-grid">
                {rewards.map((reward, idx) => (
                  <RewardCard key={`${reward.id || 'reward'}-${idx}`} reward={reward} />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 w-full" data-testid="products-grid">
                {products.map((product, idx) => (
                  <ProductCard key={`${product.product_id || 'prod'}-${idx}`} product={product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground font-body text-lg">No products found</p>
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="mt-4 rounded-full"
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12" data-testid="pagination">
                {getPageNumbers().map((p, idx) => (
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-3 py-2 text-muted-foreground flex items-center font-body text-sm font-semibold">…</span>
                  ) : (
                    <button
                      key={`page-${p}-${idx}`}
                      onClick={() => {
                        setPage(p);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-10 h-10 rounded-full font-body text-sm font-bold transition-all ${page === p
                        ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-110'
                        : 'bg-surface text-secondary hover:bg-gray-200 border border-transparent'
                        }`}
                    >
                      {p}
                    </button>
                  )
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
